import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canPurgeTenants, canReadAnkhCustomers, canWriteAnkhCustomers, canWritePlatformTenants } from "../../components/nav";
import { ANKH_SELLER } from "../../api/ankhCustomers";
import { waitLabel } from "../../portal/resend";
import {
  ASSISTED_TYPES,
  KIND_LABEL,
  SMS_LABEL,
  assistedNamespaceOK,
  createAssisted,
  STATE_LABEL,
  createInvite,
  createPromotion,
  endPromotion,
  getTenant,
  isIndianMobile,
  listInvites,
  listPromotions,
  listTenants,
  previewPurge,
  promotionBenefit,
  purgeNamespace,
  resendInvite,
  revokeInvite,
  setTenantActivation,
  extendTrial,
  setTenantState,
  type AssistedInput,
  type AssistedResult,
  type InviteInput,
  type PurgePreview,
  type PurgeResult,
  type PlatformPromotion,
  type PlatformTenant,
  type PromotionInput,
  type TenantActivation,
} from "../../api/platform";
import { endTenantPlan, getTenantPlan, monthToDateText, subscribeTenantPlan, suggestedPlan, withGst } from "../../api/tenantPlan";



const istDay = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "";
const istTime = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const kindLabel = (k: string) => KIND_LABEL[k] ?? k;
const stateLabel = (s: string) => STATE_LABEL[s] ?? s;

const STATE_COLOR: Record<string, "default" | "success" | "info" | "warning" | "error"> = {
  ACTIVE: "success",
  TRIAL: "info",
  OVERDUE: "warning",
  RESTRICTED: "error",
  SUSPENDED: "error",
  CLOSED: "default",
};

function Failure({ error }: { error: unknown }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      {error instanceof Error ? error.message : String(error)}
    </Alert>
  );
}

/**
 * Platform (owner 2026-10-24): io.ankhos as the platform owner -- its tenants
 * (operators, cloud tenants, companies) and their commercial state,
 * invitations to join, and promotions. io.ankhos superadmin only; the gateway
 * refuses any other namespace.
 */
export function PlatformScreen() {
  const identity = useIdentity();
  const [tab, setTab] = useState(0);
  if (identity.namespace !== ANKH_SELLER) {
    return (
      <Alert severity="info">
        The platform is managed at {ANKH_SELLER}. Sign in there, or come back from the provider you entered.
      </Alert>
    );
  }
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Platform
      </Typography>
      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Clients" />
        <Tab label="Invitations" />
        <Tab label="Promotions" />
      </Tabs>
      {tab === 0 && <TenantsTab />}
      {tab === 1 && <InvitationsTab />}
      {tab === 2 && <PromotionsTab />}
    </Box>
  );
}

function TenantsTab() {
  const identity = useIdentity();
  const [env, setEnv] = useState<"" | "live" | "test">("live");
  const [open, setOpen] = useState<PlatformTenant | null>(null);
  const [assisting, setAssisting] = useState(false);
  const q = useQuery({
    queryKey: ["platform-tenants", env],
    queryFn: ({ signal }) => listTenants(identity, env, signal),
    retry: false,
  });
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={env} onChange={(_, v: "" | "live" | "test" | null) => v !== null && setEnv(v)}>
          <ToggleButton value="live">Live</ToggleButton>
          <ToggleButton value="test">Test</ToggleButton>
          <ToggleButton value="">All</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Test tenants are the test bed: shown on test.ankhos.io only, and may be deleted after testing.
        </Typography>
        {canWritePlatformTenants(identity.roles) && (
          <Button variant="contained" onClick={() => setAssisting(true)}>
            Assisted onboarding
          </Button>
        )}
      </Stack>
      {assisting && <AssistedDialog onClose={() => setAssisting(false)} />}
      {q.error && <Failure error={q.error} />}
      {q.data && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Tenants">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Namespace</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Since</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {q.data.tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No tenants.</TableCell>
                </TableRow>
              )}
              {q.data.tenants.map((t) => (
                <TableRow key={t.accountid}>
                  <TableCell>
                    {t.legalname} {t.environment === "test" && <Chip size="small" label="TEST" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{kindLabel(t.kind)}</TableCell>
                  <TableCell>{t.namespace ?? "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATE_COLOR[t.state] ?? "default"} label={stateLabel(t.state)} />
                  </TableCell>
                  <TableCell>{istDay(t.statesince)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setOpen(t)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {open && <TenantDialog tenant={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function TenantDialog({ tenant, onClose }: { tenant: PlatformTenant; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const detail = useQuery({
    queryKey: ["platform-tenant", tenant.accountid],
    queryFn: () => getTenant(identity, tenant.accountid),
    retry: false,
  });
  const t = detail.data?.tenant ?? tenant;

  async function change() {
    setBusy(true);
    setError(null);
    try {
      await setTenantState(identity, t.accountid, to, reason);
      setTo("");
      setReason("");
      await Promise.all([qc.invalidateQueries({ queryKey: ["platform-tenants"] }), detail.refetch()]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t.legalname}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {kindLabel(t.kind)} · {t.environment === "live" ? "Live" : "Test"} · {t.namespace ?? "no namespace"} · joined by {t.source}
          {t.promotioncode ? ` · promotion ${t.promotioncode}` : ""}
          {t.trialendsat ? ` · trial ends ${istDay(t.trialendsat)}` : ""}
        </Typography>
        <Typography variant="subtitle2">
          State: {stateLabel(t.state)} since {istDay(t.statesince)}
          {t.statereason ? ` (${t.statereason})` : ""}
        </Typography>
        {error !== null && <Failure error={error} />}
        {!canWritePlatformTenants(identity.roles) ? null : t.nextstates.length > 0 ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ my: 2 }}>
            <TextField select size="small" label="Change to" value={to} onChange={(e) => setTo(e.target.value)} sx={{ minWidth: 160 }}>
              {t.nextstates.map((s) => (
                <MenuItem key={s} value={s}>
                  {stateLabel(s)}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="contained" disabled={!to || reason.trim().length < 3 || busy} onClick={() => void change()}>
              Change
            </Button>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
            No further state change is possible.
          </Typography>
        )}
        {detail.data?.activation?.applicable && (
          <ActivationSection
            accountid={t.accountid}
            activation={detail.data.activation}
            canWrite={canWritePlatformTenants(identity.roles)}
            onChanged={() => Promise.all([qc.invalidateQueries({ queryKey: ["platform-tenants"] }), detail.refetch()])}
          />
        )}
        {t.environment === "test" && t.trialendsat && canWritePlatformTenants(identity.roles) && (
          <TrialExtensionSection
            accountid={t.accountid}
            onChanged={() => Promise.all([qc.invalidateQueries({ queryKey: ["platform-tenants"] }), detail.refetch()])}
          />
        )}
        {canReadAnkhCustomers(identity.roles) && <PlanSection accountid={t.accountid} />}
        <Typography variant="subtitle2" gutterBottom>
          History
        </Typography>
        {detail.error && <Failure error={detail.error} />}
        <Table size="small" aria-label="History">
          <TableBody>
            {(detail.data?.events ?? []).map((e, i) => (
              <TableRow key={i}>
                <TableCell>{istTime(e.at)}</TableCell>
                <TableCell>
                  {e.fromstate ? `${stateLabel(e.fromstate)} → ` : ""}
                  {stateLabel(e.tostate)}
                </TableCell>
                <TableCell>{e.reason}</TableCell>
                <TableCell>{e.actor}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {t.environment === "test" && t.namespace && canPurgeTenants(identity.roles) && <PurgeSection tenantNamespace={t.namespace} onPurgedTenant={onClose} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Going live (owner 2026-11-01): makes this operator's invoices real tax
 * invoices and starts its AnkhOS billing, together. Staff only; the server
 * decides what is allowed, and this shows its reasons.
 */
function ActivationSection({ accountid, activation: a, canWrite, onChanged }: {
  accountid: string;
  activation: TenantActivation;
  canWrite: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const identity = useIdentity();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const goingLive = !a.activated;

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await setTenantActivation(identity, accountid, goingLive, reason);
      setConfirming(false);
      setReason("");
      await onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const blocked = goingLive && a.blockers.length > 0;
  return (
    <Box sx={{ my: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Activation:</Typography>
        <Chip size="small" color={a.activated ? "success" : "default"} label={a.activated ? "Live" : "Test"} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {a.activated ? "Invoices are real tax documents and AnkhOS billing is on." : "Invoices are TEST documents, not tax invoices."}
      </Typography>
      {error !== null && <Failure error={error} />}
      {blocked && (
        <Alert severity="info" sx={{ mb: 1 }}>
          To go live, first fix: {a.blockers.join("; ")}.
        </Alert>
      )}
      {!goingLive && a.revertblocked && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Cannot go back to test: {a.revertblocked}.
        </Alert>
      )}
      {canWrite && !blocked && !(a.activated && a.revertblocked) && !confirming && (
        <Button variant="outlined" onClick={() => setConfirming(true)}>
          {goingLive ? "Go live" : "Back to test"}
        </Button>
      )}
      {confirming && (
        <Box>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {goingLive
              ? "Its next invoices will be real tax documents in the GST record, and AnkhOS billing starts. Once a real invoice exists it cannot go back to test."
              : "Its next invoices will be TEST documents and AnkhOS stops billing it."}
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="contained" color="warning" disabled={reason.trim().length < 3 || busy} onClick={() => void apply()}>
              {goingLive ? "Confirm go live" : "Confirm back to test"}
            </Button>
            <Button disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/**
 * One extension of an operator's trial (owner 2026-11-02): staff only, once,
 * 1 to 45 days, with a reason. The server refuses a second.
 */
function TrialExtensionSection({ accountid, onChanged }: { accountid: string; onChanged: () => Promise<unknown> }) {
  const identity = useIdentity();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState("15");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = Number(days);
  const valid = Number.isInteger(n) && n >= 1 && n <= 45 && reason.trim().length >= 3;

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await extendTrial(identity, accountid, n, reason);
      setOpen(false);
      setReason("");
      await onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ my: 2 }}>
      {error !== null && <Failure error={error} />}
      {!open ? (
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Extend trial
        </Button>
      ) : (
        <Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            A trial is extended once only. The days count from today if the trial has already ended.
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField size="small" label="Days (1 to 45)" value={days} onChange={(e) => setDays(e.target.value)} sx={{ width: 140 }} />
            <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="contained" disabled={!valid || busy} onClick={() => void apply()}>
              Extend
            </Button>
            <Button disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

const inr = (n: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);

/**
 * The tenant's io.ankhos plan (owner 2026-10-25). An operator's band is
 * preselected from its active subscriber count; a cloud tenant chooses. The
 * first period is the trial and the gateway sets every date -- this only shows
 * them, and says when the first invoice falls.
 */
function PlanSection({ accountid }: { accountid: string }) {
  const identity = useIdentity();
  const canWrite = canWriteAnkhCustomers(identity.roles);
  const q = useQuery({
    queryKey: ["tenant-plan", accountid],
    queryFn: ({ signal }) => getTenantPlan(identity, accountid, signal),
    retry: false,
  });
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState("");
  const v = q.data;
  const suggested = v ? suggestedPlan(v) : undefined;
  const pick = chosen || suggested?.componentid || "";

  async function run(f: () => Promise<string>) {
    setBusy(true);
    setError(null);
    try {
      setNote(await f());
      setChosen("");
      await q.refetch();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Plan
      </Typography>
      {q.error && <Failure error={q.error} />}
      {error !== null && <Failure error={error} />}
      {note && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNote("")}>
          {note}
        </Alert>
      )}
      {v && v.held.length > 0 && (
        <Table size="small" aria-label="Plan">
          <TableBody>
            {v.held.map((h) => (
              <TableRow key={h.subcomponentid}>
                <TableCell>
                  {h.name} · {inr(h.price)} + GST/month
                </TableCell>
                <TableCell>
                  {istDay(h.periodstart)} → {istDay(h.periodend)}
                  {!h.invoiced && " (not invoiced)"}
                </TableCell>
                <TableCell>
                  {h.billinginterval === "ARREARS"
                    ? "billed monthly in arrears, on the band of the month's average active subscribers"
                    : `next invoice ${istDay(h.nextrenewal)}`}
                </TableCell>
                <TableCell align="right">
                  {canWrite && (
                    <Button
                      size="small"
                      color="error"
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(`End ${h.name} for this tenant? Its renewals stop; nothing already invoiced changes.`)) return;
                        void run(async () => {
                          await endTenantPlan(identity, accountid, h.subcomponentid);
                          return `${h.name} ended.`;
                        });
                      }}
                    >
                      End plan
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {v?.monthtodate?.map((e) => (
        <Typography key={e.subcomponentid} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {monthToDateText(e)}
        </Typography>
      ))}
      {v?.monthtodateerror && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          {v.monthtodateerror}
        </Typography>
      )}
      {v && v.held.length === 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Not on a plan.
            {v.product === "ankhos" && v.activesubscribers !== undefined &&
              ` ${v.activesubscribers.toLocaleString("en-IN")} active subscribers${suggested ? `, so ${suggested.name}` : ""}.`}
            {v.trialdays > 0 ? ` First ${v.trialdays} days free, then billed from the next 1st.` : " Billed from today."}
            {!v.accountopen && " Billing account not open yet (Billing › Customers)."}
          </Typography>
          {canWrite && v.plans.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                size="small"
                label={v.product === "ankhos" ? "Band" : "Plan"}
                value={pick}
                onChange={(e) => setChosen(e.target.value)}
                sx={{ minWidth: 260 }}
              >
                {v.plans.map((p) => (
                  <MenuItem key={p.componentid} value={p.componentid}>
                    {p.name} — {inr(p.price)} + GST ({inr(withGst(p.price, p.gstpercent))})
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                disabled={!pick || busy}
                onClick={() =>
                  void run(async () => {
                    const r = await subscribeTenantPlan(identity, accountid, pick);
                    return `${r.plan}: ${r.note}`;
                  })
                }
              >
                Put on plan
              </Button>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

const EMPTY_ASSISTED: AssistedInput = {
  kind: "operator", operatortype: "ISP", namespace: "", legalname: "", firstname: "", lastname: "",
  mobile: "", email: "", environment: "live", promotioncode: "",
};

/** The /join page for the owner's mobile, opened on this computer (staff screen path). */
export function joinLinkFor(mobile: string): string {
  return `/join?mobile=${encodeURIComponent(mobile.replace(/\D/g, "").slice(-10))}`;
}

/**
 * Assisted onboarding (owner 2026-10-24): staff fill in the business; the
 * owner proves their OWN mobile -- from the SMS on their phone, or here, typing
 * the code their phone receives into /join. Staff never see that code.
 */
function AssistedDialog({ onClose }: { onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [f, setF] = useState<AssistedInput>(EMPTY_ASSISTED);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<AssistedResult | null>(null);
  const mobileBad = f.mobile.trim() !== "" && !isIndianMobile(f.mobile);
  const nsBad = f.namespace.trim() !== "" && !assistedNamespaceOK(f.namespace);
  const ok = f.legalname.trim() && f.firstname.trim() && f.lastname.trim() && isIndianMobile(f.mobile) && assistedNamespaceOK(f.namespace);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      setDone(await createAssisted(identity, f));
      await Promise.all([qc.invalidateQueries({ queryKey: ["platform-tenants"] }), qc.invalidateQueries({ queryKey: ["platform-invites"] })]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{f.legalname} is prepared</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {done.namespace} is in Clients as Invited. The SMS is {done.sms}.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the owner can verify their mobile. Either they open the SMS link on their own phone, or open the sign-up page
            here: hand them this screen, they type the code their phone receives and choose a password. You never see the code.
          </Alert>
          <Button variant="outlined" href={joinLinkFor(f.mobile)} target="_blank" rel="noopener">
            Verify on this screen
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Afterwards the tenant moves to Applied. Complete their details, then approve it as usual.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Assisted onboarding</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          For a business we help sign up. We prepare it now; its owner verifies their own mobile to get the login.
        </Typography>
        {error !== null && <Failure error={error} />}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select size="small" label="Kind" value={f.kind} sx={{ flex: 1 }}
              onChange={(e) => {
                const kind = e.target.value as AssistedInput["kind"];
                setF({ ...f, kind, operatortype: ASSISTED_TYPES[kind][0]!.code });
              }}>
              <MenuItem value="operator">{kindLabel("operator")}</MenuItem>
              <MenuItem value="cloud_tenant">{kindLabel("cloud_tenant")}</MenuItem>
            </TextField>
            <TextField select size="small" label="Type" value={f.operatortype} sx={{ flex: 2 }} onChange={(e) => setF({ ...f, operatortype: e.target.value })}>
              {ASSISTED_TYPES[f.kind].map((t) => (
                <MenuItem key={t.code} value={t.code}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField required size="small" label="Legal name" value={f.legalname} onChange={(e) => setF({ ...f, legalname: e.target.value })} />
          <TextField
            required
            size="small"
            label="Namespace"
            value={f.namespace}
            onChange={(e) => setF({ ...f, namespace: e.target.value })}
            error={nsBad}
            helperText={nsBad ? "Three or more parts, such as net.ankhnet.shreenet" : "Such as net.ankhnet.shreenet -- cannot be changed later"}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField required size="small" label="Owner first name" value={f.firstname} sx={{ flex: 1 }} onChange={(e) => setF({ ...f, firstname: e.target.value })} />
            <TextField required size="small" label="Owner last name" value={f.lastname} sx={{ flex: 1 }} onChange={(e) => setF({ ...f, lastname: e.target.value })} />
          </Stack>
          <TextField
            required
            size="small"
            label="Owner's mobile"
            value={f.mobile}
            onChange={(e) => setF({ ...f, mobile: e.target.value })}
            error={mobileBad}
            helperText={mobileBad ? "A 10-digit Indian mobile" : "Their own number: only they can verify it"}
          />
          <TextField size="small" label="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} helperText="They verify it themselves later" />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select size="small" label="Environment" value={f.environment} sx={{ flex: 1 }}
              onChange={(e) => setF({ ...f, environment: e.target.value as AssistedInput["environment"] })}>
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </TextField>
            <TextField size="small" label="Promotion code" value={f.promotioncode} sx={{ flex: 1 }}
              onChange={(e) => setF({ ...f, promotioncode: e.target.value })} helperText="Blank = the standard trial" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={!ok || busy} onClick={() => void save()}>
          {busy ? "Preparing…" : "Prepare"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Resend SMS, after the gateway's wait (owner 2026-10-23: every send has a
 * Resend after a wait, with a countdown). `resendin` was measured when the list
 * was fetched at `fetchedAt`; the button counts down from there.
 */
function ResendButton({ resendin, fetchedAt, onResend }: { resendin: number; fetchedAt: number; onResend: () => Promise<void> }) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const left = Math.max(0, Math.ceil(resendin - (now - fetchedAt) / 1000));
  useEffect(() => {
    if (left <= 0) return;
    const id = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(id);
  }, [left, now]);
  return (
    <Button
      size="small"
      disabled={left > 0 || busy}
      onClick={() => {
        setBusy(true);
        void onResend().finally(() => setBusy(false));
      }}
    >
      {busy ? "Sending…" : waitLabel("Resend SMS", left)}
    </Button>
  );
}

/**
 * Delete test tenant (owner 2026-10-24). Test tenants only -- the gateway
 * refuses anything live, the platform's own namespaces and bare parents. The
 * tenant itself, or one namespace inside it, after a preview and with the
 * namespace typed again. Rows and logins are deleted for good; the audit log
 * keeps the record.
 */
function PurgeSection({ tenantNamespace, onPurgedTenant }: { tenantNamespace: string; onPurgedTenant: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [target, setTarget] = useState(tenantNamespace);
  const [preview, setPreview] = useState<PurgePreview | null>(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<PurgeResult | null>(null);

  async function look() {
    setBusy(true);
    setError(null);
    setPreview(null);
    setConfirm("");
    try {
      setPreview(await previewPurge(identity, target));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function purge() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await purgeNamespace(identity, preview.namespace, confirm);
      setDone(r);
      setPreview(null);
      await qc.invalidateQueries({ queryKey: ["platform-tenants"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ mt: 3, p: 2, border: 1, borderColor: "error.main", borderRadius: 1 }}>
      <Typography variant="subtitle2" color="error" gutterBottom>
        Delete test tenant
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Removes the namespace, every namespace under it, all their records and their logins, for good. Test tenants only.
      </Typography>
      {error !== null && <Failure error={error} />}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Deleted {done.namespace}: {done.rows} records, {(done.loginsdeleted ?? []).length} logins.
          {(done.loginsfailed ?? []).length > 0 && ` Not deleted (try again): ${(done.loginsfailed ?? []).join(", ")}.`}
          {done.loginnote && ` ${done.loginnote}`}
          {done.namespace === tenantNamespace && (
            <Button size="small" sx={{ ml: 1 }} onClick={onPurgedTenant}>
              Close
            </Button>
          )}
        </Alert>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Namespace to delete"
          value={target}
          onChange={(e) => {
            setTarget(e.target.value);
            setPreview(null);
          }}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" disabled={busy || !target.trim()} onClick={() => void look()}>
          Preview
        </Button>
      </Stack>
      {preview && !preview.allowed && <Alert severity="warning">Cannot delete {preview.namespace}: {preview.reason}</Alert>}
      {preview?.allowed && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {preview.rows ?? 0} records in {(preview.tables ?? []).length} tables
            {preview.istenant ? ", and the tenant itself" : ""}. Namespaces: {(preview.namespaces ?? []).join(", ") || preview.namespace}.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Logins:{" "}
            {preview.loginscheckable === false
              ? "not checked (ZITADEL not connected) -- none will be deleted"
              : (preview.logins ?? []).map((l) => `${l.username} (${l.namespace})`).join(", ") || "none"}
            {preview.loginerror ? ` -- ${preview.loginerror}` : ""}
          </Typography>
          <Table size="small" aria-label="Records to delete" sx={{ mb: 2 }}>
            <TableBody>
              {(preview.tables ?? []).map((c) => (
                <TableRow key={`${c.table}.${c.column}`}>
                  <TableCell>{c.table}</TableCell>
                  <TableCell>{c.column}</TableCell>
                  <TableCell align="right">{c.rows}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              size="small"
              label={`Type ${preview.namespace} to confirm`}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" color="error" disabled={busy || confirm.trim() !== preview.namespace} onClick={() => void purge()}>
              {busy ? "Deleting…" : "Delete for good"}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

const EMPTY_INVITE: InviteInput = { kind: "operator", name: "", mobile: "", email: "", promotioncode: "" };

function InvitationsTab() {
  const identity = useIdentity();
  const canWrite = canWritePlatformTenants(identity.roles);
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<InviteInput>(EMPTY_INVITE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["platform-invites"], queryFn: ({ signal }) => listInvites(identity, signal), retry: false });

  const mobileBad = f.mobile.trim() !== "" && !isIndianMobile(f.mobile);
  const ok = f.name.trim() && isIndianMobile(f.mobile);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await createInvite(identity, f);
      setAdding(false);
      setF(EMPTY_INVITE);
      setNotice(`Invitation recorded for ${r.invite.name}, open until ${istDay(r.invite.expiresat)}. ${r.sms ?? ""}`);
      await qc.invalidateQueries({ queryKey: ["platform-invites"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function resend(id: string, name: string) {
    setError(null);
    try {
      const r = await resendInvite(identity, id);
      setNotice(`Invitation SMS to ${name} ${r.sms}`);
    } catch (e) {
      setError(e);
    }
    await qc.invalidateQueries({ queryKey: ["platform-invites"] });
  }

  async function revoke(id: string) {
    setError(null);
    try {
      await revokeInvite(identity, id);
      await qc.invalidateQueries({ queryKey: ["platform-invites"] });
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        An invitation is bound to the business's mobile and stays open 7 days. The person opens ankhos.io/join, enters that mobile and
        proves it with a code.{" "}
        {q.data && (q.data.smsready
          ? "An SMS goes out between 10:00 and 21:00 IST; numbers on DND or opted out get none, so also tell the person yourself."
          : "The invitation SMS is switched off: tell the person yourself.")}
      </Alert>
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error !== null && <Failure error={error} />}
      {q.error && <Failure error={q.error} />}
      {canWritePlatformTenants(identity.roles) && <Button variant="contained" sx={{ mb: 2 }} onClick={() => {
          setError(null);
          setAdding(true);
        }}>
        Invite
      </Button>}
      {q.data && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Invitations">
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Promotion</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>SMS</TableCell>
                <TableCell>Open until</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {q.data.invites.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>No invitations yet.</TableCell>
                </TableRow>
              )}
              {q.data.invites.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.name}</TableCell>
                  <TableCell>{kindLabel(i.kind)}</TableCell>
                  <TableCell>{i.mobile}</TableCell>
                  <TableCell>{i.promotioncode ?? "Standard trial"}</TableCell>
                  <TableCell>{i.status}</TableCell>
                  <TableCell title={i.smserror ?? ""}>{i.smsstatus ? (SMS_LABEL[i.smsstatus] ?? i.smsstatus) : "—"}</TableCell>
                  <TableCell>{istDay(i.expiresat)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {canWrite && q.data.smsready && i.resendin !== undefined && (
                      <ResendButton resendin={i.resendin} fetchedAt={q.dataUpdatedAt} onResend={() => resend(i.id, i.name)} />
                    )}
                    {canWrite && (i.status === "SENT" || i.status === "OPENED") && (
                      <Button size="small" color="error" onClick={() => void revoke(i.id)}>
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {adding && (
        <Dialog open onClose={() => setAdding(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Invite a business</DialogTitle>
          <DialogContent>
            {error !== null && <Failure error={error} />}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField select size="small" label="Kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                {(q.data?.kinds.length ? q.data.kinds : ["operator"]).map((k) => (
                  <MenuItem key={k} value={k}>
                    {kindLabel(k)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField required size="small" label="Business name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <TextField
                required
                size="small"
                label="Mobile"
                value={f.mobile}
                onChange={(e) => setF({ ...f, mobile: e.target.value })}
                error={mobileBad}
                helperText={mobileBad ? "A 10-digit Indian mobile" : "The invitation is bound to this number"}
              />
              <TextField size="small" label="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <TextField
                size="small"
                label="Promotion code"
                value={f.promotioncode}
                onChange={(e) => setF({ ...f, promotioncode: e.target.value })}
                helperText="Blank = the standard trial"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="contained" disabled={!ok || busy} onClick={() => void send()}>
              {busy ? "Saving…" : "Invite"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

const EMPTY_PROMO: PromotionInput = { code: "", name: "", benefit: "free_days", value: 45, kinds: ["operator"], isstandard: false };

function PromotionsTab() {
  const identity = useIdentity();
  const canWrite = canWritePlatformTenants(identity.roles);
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<PromotionInput>(EMPTY_PROMO);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const q = useQuery({ queryKey: ["platform-promotions"], queryFn: ({ signal }) => listPromotions(identity, signal), retry: false });
  const discount = f.benefit !== "free_days";

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await createPromotion(identity, { ...f, months: discount ? f.months : undefined, validto: f.validto || undefined, validfrom: f.validfrom || undefined });
      setAdding(false);
      setF(EMPTY_PROMO);
      await qc.invalidateQueries({ queryKey: ["platform-promotions"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function end(p: PlatformPromotion) {
    setError(null);
    try {
      await endPromotion(identity, p.code);
      await qc.invalidateQueries({ queryKey: ["platform-promotions"] });
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        The standard trial is a promotion too. To change it, end it and add a new standard one: tenants keep the trial they were given.
      </Alert>
      {error !== null && <Failure error={error} />}
      {q.error && <Failure error={q.error} />}
      {canWrite && <Button variant="contained" sx={{ mb: 2 }} onClick={() => setAdding(true)}>
        Add promotion
      </Button>}
      {q.data && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Promotions">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Benefit</TableCell>
                <TableCell>For</TableCell>
                <TableCell>Valid</TableCell>
                <TableCell>Used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {q.data.promotions.map((p) => (
                <TableRow key={p.code}>
                  <TableCell>
                    {p.code} {p.isstandard && <Chip size="small" label="Standard" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{promotionBenefit(p)}</TableCell>
                  <TableCell>{p.kinds.map(kindLabel).join(", ")}</TableCell>
                  <TableCell>
                    {istDay(p.validfrom)}
                    {p.validto ? ` – ${istDay(p.validto)}` : " onwards"}
                  </TableCell>
                  <TableCell>
                    {p.usedcount}
                    {p.maxuses ? ` / ${p.maxuses}` : ""}
                  </TableCell>
                  <TableCell>{p.status === "ACTIVE" ? "Active" : "Ended"}</TableCell>
                  <TableCell align="right">
                    {canWrite && p.status === "ACTIVE" && (
                      <Button size="small" color="error" onClick={() => void end(p)}>
                        End
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {adding && (
        <Dialog open onClose={() => setAdding(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add promotion</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField required size="small" label="Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} sx={{ width: 160 }} />
                <TextField required size="small" label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Benefit"
                  value={f.benefit}
                  onChange={(e) => setF({ ...f, benefit: e.target.value as PromotionInput["benefit"] })}
                  sx={{ width: 180 }}
                >
                  <MenuItem value="free_days">Free days</MenuItem>
                  <MenuItem value="percent_off">% off</MenuItem>
                  <MenuItem value="amount_off">₹ off</MenuItem>
                </TextField>
                <TextField size="small" type="number" label="Value" value={f.value} onChange={(e) => setF({ ...f, value: Number(e.target.value) })} sx={{ width: 120 }} />
                {discount && (
                  <TextField
                    size="small"
                    type="number"
                    label="For invoices"
                    value={f.months ?? ""}
                    onChange={(e) => setF({ ...f, months: e.target.value ? Number(e.target.value) : undefined })}
                    sx={{ width: 130 }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="date" label="Valid from" InputLabelProps={{ shrink: true }} value={f.validfrom ?? ""} onChange={(e) => setF({ ...f, validfrom: e.target.value })} />
                <TextField size="small" type="date" label="Valid to" InputLabelProps={{ shrink: true }} value={f.validto ?? ""} onChange={(e) => setF({ ...f, validto: e.target.value })} />
                <TextField
                  size="small"
                  type="number"
                  label="Max uses"
                  value={f.maxuses ?? ""}
                  onChange={(e) => setF({ ...f, maxuses: e.target.value ? Number(e.target.value) : undefined })}
                  sx={{ width: 120 }}
                />
              </Stack>
              <Stack direction="row" flexWrap="wrap">
                {(q.data?.kinds.length ? q.data.kinds : ["operator"]).map((k) => (
                  <FormControlLabel
                    key={k}
                    label={kindLabel(k)}
                    control={
                      <Checkbox
                        checked={f.kinds.includes(k)}
                        onChange={(e) => setF({ ...f, kinds: e.target.checked ? [...f.kinds, k] : f.kinds.filter((x) => x !== k) })}
                      />
                    }
                  />
                ))}
              </Stack>
              {f.benefit === "free_days" && (
                <FormControlLabel
                  label="This is the standard trial (every tenant with no code gets it)"
                  control={<Checkbox checked={f.isstandard} onChange={(e) => setF({ ...f, isstandard: e.target.checked })} />}
                />
              )}
              {error !== null && <Failure error={error} />}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="contained" disabled={!f.code.trim() || !f.name.trim() || f.kinds.length === 0 || busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
